import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Shared loading indicator with optional title, message, and framing.
 */
@Component({
  selector: 'app-loading-spinner',
  standalone: true,
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col items-center justify-center text-center" [ngClass]="containerClass()">
      <div class="flex items-center justify-center rounded" [ngClass]="badgeClass()" style="background:rgb(var(--talos-ink-rgb) / 0.06)">
        <div class="rounded-full border-talos-ink border-t-transparent animate-spin" [ngClass]="spinnerClass()"></div>
      </div>

      @if (title()) {
        <h3 class="mt-4 font-semibold text-talos-ink" [ngClass]="titleClass()">{{ title() }}</h3>
      }

      @if (message()) {
        <p class="mt-1" [ngClass]="messageClass()" style="color:rgb(var(--talos-ink-rgb) / 0.5)">{{ message() }}</p>
      }
    </div>
  `,
})
export class LoadingSpinnerComponent {
  /** Optional title displayed below the spinner. */
  readonly title   = input<string>('');
  /** Optional supporting copy displayed below the title. */
  readonly message = input<string>('');
  /** Visual spinner size. */
  readonly size    = input<'sm' | 'md' | 'lg'>('md');
  /** Whether the spinner should render inside a bordered white frame. */
  readonly framed  = input(false);
  /** Whether the spinner should fill a larger loading area. */
  readonly fill    = input(false);

  /** Container classes derived from framing and fill options. */
  protected readonly containerClass = computed(() => ({
    'min-h-[220px] px-6 py-10': this.fill() && !this.framed(),
    'rounded bg-[var(--talos-bg)] px-6 py-10': this.framed(),
  }));

  /** Size classes for the circular badge behind the spinner. */
  protected readonly badgeClass = computed(() => {
    switch (this.size()) {
      case 'sm': return 'h-10 w-10';
      case 'lg': return 'h-16 w-16';
      default: return 'h-14 w-14';
    }
  });

  /** Size and border classes for the spinning ring. */
  protected readonly spinnerClass = computed(() => {
    switch (this.size()) {
      case 'sm': return 'h-5 w-5 border-2';
      case 'lg': return 'h-8 w-8 border-[3px]';
      default: return 'h-7 w-7 border-[3px]';
    }
  });

  /** Heading size classes aligned to the spinner size. */
  protected readonly titleClass = computed(() => this.size() === 'sm' ? 'text-base' : 'text-lg');
  /** Message size classes aligned to the spinner size. */
  protected readonly messageClass = computed(() => this.size() === 'sm' ? 'text-xs' : 'text-sm');
}
